import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { getErrorMessage } from '@core/api/error-handler';
import { captureApiErrorInPostHog } from '@core/api/capture-api-error-in-posthog';
import { useAuthStore } from '@core/store/auth-store';
import { apiClient } from '@core/api/request/api-client';
import { fetchRefreshToken } from '@core/api/fetch-refresh-token';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null
) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

export const responseErrorInterceptor = async (error: AxiosError) => {
  const originalRequest =
    error.config as unknown as InternalAxiosRequestConfig & {
      _retry?: boolean;
      preserveError?: boolean;
    };

  if (error.response?.status === 401 && !originalRequest._retry) {
    if (isRefreshing) {
      return new Promise<string | null>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => {
          return apiClient(originalRequest);
        })
        .catch((err) => Promise.reject(getErrorMessage(err)));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const accessToken = await fetchRefreshToken();

      if (accessToken) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        useAuthStore.getState().setAccessToken(accessToken);
        processQueue(null);
        return apiClient(originalRequest);
      } else {
        processQueue(error);
        return Promise.reject(error);
      }
    } catch (refreshError) {
      processQueue(refreshError as AxiosError);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }

  captureApiErrorInPostHog(error);
  /*
    Opt-in escape hatch, mirroring `withOrg` on the request side: a per-call
    config flag, read here, that changes nothing for anyone who does not set it.

    The default below flattens the rejection to a human sentence, which is what
    the app's many `onError: (error: string) => toast(error)` handlers consume —
    but it also destroys the response body, and some endpoints answer with a
    MACHINE-readable discriminator alongside the prose (the inbox composer
    branches on `category`; the sentence is the server's to reword at will).
    Re-deriving that from the flattened text would be string-matching the very
    prose the structured contract exists to avoid, so callers that need the body
    ask for the raw error instead of the whole app changing shape.
  */
  if (originalRequest?.preserveError) return Promise.reject(error);
  return Promise.reject(getErrorMessage(error));
};
